import {
  useCallback,
  useEffect,
  useReducer,
  useRef,
} from 'react'

import type { EmbeddingProfileId } from '../data/embeddingProfiles'
import {
  EmbeddingClient,
  EmbeddingClientError,
  type EmbeddingClientEvent,
  type EmbeddingReady,
  type EmbeddingResult,
} from './client'
import {
  INITIAL_SEMANTIC_STATE,
  semanticCoordinatorReducer,
  type SemanticCoordinatorState,
} from './state'
import type { SetupProgress } from './protocol'

const SEMANTIC_PROFILE_ID = 'snowflake-xs' as const
const DEFAULT_DEBOUNCE_MS = 250

type NavigatorWithConnection = Navigator & {
  connection?: EventTarget & {
    saveData?: boolean
  }
}

type IdleWindow = Window & {
  requestIdleCallback?: (
    callback: () => void,
  ) => number
  cancelIdleCallback?: (handle: number) => void
}

export type AutomaticPreloadEnvironment = {
  visible: boolean
  online: boolean
  saveData: boolean
}

export type AutomaticPreloadDeferral =
  | 'hidden'
  | 'offline'
  | 'save-data'
  | null

export function automaticPreloadDeferral(
  environment: AutomaticPreloadEnvironment,
): AutomaticPreloadDeferral {
  if (!environment.visible) return 'hidden'
  if (!environment.online) return 'offline'
  if (environment.saveData) return 'save-data'
  return null
}

export interface SemanticEmbeddingClient {
  preload(): Promise<EmbeddingReady>
  compare(prompt: string): Promise<EmbeddingResult>
  pause(reason?: string): void
  dispose(reason?: string): void
}

export type SemanticClientFactory = (
  profileId: EmbeddingProfileId,
  onEvent: (event: EmbeddingClientEvent) => void,
) => SemanticEmbeddingClient

export type SemanticPreloadScheduler = (
  run: () => void,
) => () => void

export type UseSemanticAnalysisOptions = {
  debounceMs?: number
  clientFactory?: SemanticClientFactory
  schedulePreload?: SemanticPreloadScheduler
}

export type SemanticAnalysisController =
  SemanticCoordinatorState & {
    profileId: typeof SEMANTIC_PROFILE_ID
    pauseSetup(): void
    retrySetup(): void
  }

type PromptRevision = {
  prompt: string
  revision: number
}

function defaultClientFactory(
  profileId: EmbeddingProfileId,
  onEvent: (event: EmbeddingClientEvent) => void,
): SemanticEmbeddingClient {
  if (typeof Worker === 'undefined') {
    const unavailable = () =>
      Promise.reject(
        new EmbeddingClientError({
          code: 'WORKER_FAILED',
          phase: 'load',
          retryable: false,
          message:
            'This browser does not provide Web Worker support.',
        }),
      )
    return {
      preload: unavailable,
      compare: unavailable,
      pause: () => {},
      dispose: () => {},
    }
  }
  return new EmbeddingClient(profileId, onEvent)
}

function scheduleAfterFirstPaint(
  run: () => void,
): () => void {
  const idleWindow = window as IdleWindow
  let idleHandle: number | null = null
  let timeoutHandle: number | null = null
  const frameHandle = window.requestAnimationFrame(() => {
    if (idleWindow.requestIdleCallback) {
      idleHandle = idleWindow.requestIdleCallback(run)
      return
    }
    timeoutHandle = window.setTimeout(run, 0)
  })

  return () => {
    window.cancelAnimationFrame(frameHandle)
    if (
      idleHandle !== null &&
      idleWindow.cancelIdleCallback
    ) {
      idleWindow.cancelIdleCallback(idleHandle)
    }
    if (timeoutHandle !== null) {
      window.clearTimeout(timeoutHandle)
    }
  }
}

function currentEnvironment(): AutomaticPreloadEnvironment {
  const connection = (navigator as NavigatorWithConnection)
    .connection
  return {
    visible: document.visibilityState === 'visible',
    online: navigator.onLine,
    saveData: connection?.saveData === true,
  }
}

function requestId(): string {
  return crypto.randomUUID()
}

export function useSemanticAnalysis(
  prompt: string,
  options: UseSemanticAnalysisOptions = {},
): SemanticAnalysisController {
  const [state, dispatch] = useReducer(
    semanticCoordinatorReducer,
    INITIAL_SEMANTIC_STATE,
  )
  const optionsRef = useRef(options)
  const clientRef = useRef<SemanticEmbeddingClient | null>(
    null,
  )
  const mountedRef = useRef(false)
  const readyRef = useRef(false)
  const runningRef = useRef(false)
  const preloadingRef = useRef(false)
  const preloadAttemptRef = useRef(0)
  const userPausedRef = useRef(false)
  const saveDataOverrideRef = useRef(false)
  const revisionRef = useRef(0)
  const latestPromptRef = useRef<PromptRevision>({
    prompt: '',
    revision: 0,
  })
  const pendingPromptRef = useRef<PromptRevision | null>(
    null,
  )
  const debounceRef = useRef<number | null>(null)
  const cancelScheduleRef = useRef<(() => void) | null>(
    null,
  )
  const evaluatePreloadRef = useRef<() => void>(() => {})

  const drain = useCallback(async (): Promise<void> => {
    if (
      !mountedRef.current ||
      !readyRef.current ||
      runningRef.current
    ) {
      return
    }
    const pending = pendingPromptRef.current
    const client = clientRef.current
    if (!pending || !client) return

    pendingPromptRef.current = null
    runningRef.current = true
    const activeRequestId = requestId()
    dispatch({
      type: 'analysis-started',
      revision: pending.revision,
      requestId: activeRequestId,
    })

    try {
      const result = await client.compare(pending.prompt)
      if (!mountedRef.current) return
      dispatch({
        type: 'runtime-ready',
        device: result.device,
      })
      dispatch({
        type: 'analysis-succeeded',
        revision: pending.revision,
        requestId: activeRequestId,
        device: result.device,
        prompt: pending.prompt,
        matches: result.matches,
        routeDecision: result.routeDecision,
      })
    } catch (error) {
      if (!mountedRef.current) return
      if (
        error instanceof EmbeddingClientError &&
        error.code === 'CANCELLED'
      ) {
        return
      }
      dispatch({
        type: 'analysis-failed',
        revision: pending.revision,
        requestId: activeRequestId,
        message:
          error instanceof Error
            ? error.message
            : String(error),
      })
    } finally {
      runningRef.current = false
      if (
        mountedRef.current &&
        pendingPromptRef.current
      ) {
        void drain()
      }
    }
  }, [])

  const handleClientEvent = useCallback(
    (event: EmbeddingClientEvent): void => {
      if (!mountedRef.current) return
      if (event.type === 'progress') {
        const progress: SetupProgress = {
          ...event.progress,
          status:
            event.progress.status === 'ready'
              ? 'indeterminate'
              : event.progress.status,
        }
        dispatch({
          type: 'preload-progress',
          progress,
        })
        return
      }
      if (event.type === 'fallback') {
        if (readyRef.current) return
        dispatch({
          type: 'runtime-fallback',
          failedDevice: event.failedDevice,
          nextDevice: event.nextDevice,
        })
        return
      }
      readyRef.current = true
      saveDataOverrideRef.current = false
      dispatch({
        type: 'runtime-ready',
        device: event.device,
      })
      void drain()
    },
    [drain],
  )

  const startPreload = useCallback((): void => {
    const client = clientRef.current
    if (
      !mountedRef.current ||
      !client ||
      readyRef.current ||
      preloadingRef.current
    ) {
      return
    }

    cancelScheduleRef.current?.()
    cancelScheduleRef.current = null
    preloadingRef.current = true
    const attempt = preloadAttemptRef.current + 1
    preloadAttemptRef.current = attempt

    void client.preload().catch((error: unknown) => {
      if (
        !mountedRef.current ||
        attempt !== preloadAttemptRef.current ||
        (error instanceof EmbeddingClientError &&
          error.code === 'CANCELLED')
      ) {
        return
      }
      dispatch({
        type: 'runtime-failed',
        code:
          error instanceof EmbeddingClientError
            ? error.code
            : 'LOAD_FAILED',
        message:
          error instanceof Error
            ? error.message
            : String(error),
        retryable:
          error instanceof EmbeddingClientError
            ? error.retryable
            : true,
      })
    }).finally(() => {
      if (attempt === preloadAttemptRef.current) {
        preloadingRef.current = false
      }
    })
  }, [])

  const deferSetup = useCallback(
    (
      reason:
        | 'hidden'
        | 'offline'
        | 'save-data'
        | 'user-paused',
    ): void => {
      cancelScheduleRef.current?.()
      cancelScheduleRef.current = null
      if (
        preloadingRef.current ||
        runningRef.current ||
        (reason === 'user-paused' && readyRef.current)
      ) {
        preloadAttemptRef.current += 1
        preloadingRef.current = false
        readyRef.current = false
        clientRef.current?.pause(
          `Local analysis deferred: ${reason}.`,
        )
        const latest = latestPromptRef.current
        if (latest.prompt.trim()) {
          pendingPromptRef.current = latest
        }
      }
      dispatch({ type: 'runtime-deferred', reason })
    },
    [],
  )

  useEffect(() => {
    mountedRef.current = true
    const clientFactory =
      optionsRef.current.clientFactory ??
      defaultClientFactory
    clientRef.current = clientFactory(
      SEMANTIC_PROFILE_ID,
      handleClientEvent,
    )
    const schedule =
      optionsRef.current.schedulePreload ??
      scheduleAfterFirstPaint

    const evaluatePreload = (): void => {
      if (
        !mountedRef.current ||
        readyRef.current
      ) {
        return
      }
      if (userPausedRef.current) {
        deferSetup('user-paused')
        return
      }
      const environment = currentEnvironment()
      const reason = automaticPreloadDeferral({
        ...environment,
        saveData:
          environment.saveData &&
          !saveDataOverrideRef.current,
      })
      if (reason) {
        deferSetup(reason)
        return
      }
      if (preloadingRef.current) return
      if (cancelScheduleRef.current) return
      dispatch({ type: 'preload-scheduled' })
      cancelScheduleRef.current = schedule(startPreload)
    }
    evaluatePreloadRef.current = evaluatePreload

    const handleEnvironmentChange = (): void => {
      if (readyRef.current) return
      evaluatePreload()
    }
    const connection = (navigator as NavigatorWithConnection)
      .connection
    document.addEventListener(
      'visibilitychange',
      handleEnvironmentChange,
    )
    window.addEventListener('online', handleEnvironmentChange)
    window.addEventListener('offline', handleEnvironmentChange)
    connection?.addEventListener(
      'change',
      handleEnvironmentChange,
    )
    evaluatePreload()

    return () => {
      mountedRef.current = false
      preloadAttemptRef.current += 1
      cancelScheduleRef.current?.()
      cancelScheduleRef.current = null
      if (debounceRef.current !== null) {
        window.clearTimeout(debounceRef.current)
      }
      document.removeEventListener(
        'visibilitychange',
        handleEnvironmentChange,
      )
      window.removeEventListener(
        'online',
        handleEnvironmentChange,
      )
      window.removeEventListener(
        'offline',
        handleEnvironmentChange,
      )
      connection?.removeEventListener(
        'change',
        handleEnvironmentChange,
      )
      clientRef.current?.dispose()
      clientRef.current = null
    }
  }, [deferSetup, handleClientEvent, startPreload])

  useEffect(() => {
    revisionRef.current += 1
    const revision = revisionRef.current
    const next = { prompt, revision }
    latestPromptRef.current = next
    pendingPromptRef.current = null
    dispatch({
      type: 'input-changed',
      revision,
      hasPrompt: Boolean(prompt.trim()),
    })

    if (debounceRef.current !== null) {
      window.clearTimeout(debounceRef.current)
      debounceRef.current = null
    }
    if (!prompt.trim()) return

    debounceRef.current = window.setTimeout(() => {
      if (
        latestPromptRef.current.revision !== revision
      ) {
        return
      }
      pendingPromptRef.current = next
      void drain()
    }, optionsRef.current.debounceMs ?? DEFAULT_DEBOUNCE_MS)

    return () => {
      if (debounceRef.current !== null) {
        window.clearTimeout(debounceRef.current)
        debounceRef.current = null
      }
    }
  }, [drain, prompt])

  const pauseSetup = useCallback((): void => {
    userPausedRef.current = true
    saveDataOverrideRef.current = false
    deferSetup('user-paused')
  }, [deferSetup])

  const retrySetup = useCallback((): void => {
    userPausedRef.current = false
    saveDataOverrideRef.current = true
    evaluatePreloadRef.current()
  }, [])

  return {
    ...state,
    profileId: SEMANTIC_PROFILE_ID,
    pauseSetup,
    retrySetup,
  }
}
